import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Pagination } from './ui/pagination';
import { Plus, Search, Eye, Edit, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { hsnApiService, HsnCode } from '../services/hsnApi';
import { PermissionGate } from './ui/PermissionGate';

export function HSNListingPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [hsnCodes, setHsnCodes] = useState<HsnCode[]>([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const ROUTE = '/hsn';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await hsnApiService.getHsnCodes();
        setHsnCodes(data);
      } catch (e) {
        // toasted
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return hsnCodes.filter(h =>
      h.hsnCode?.toLowerCase().includes(term) ||
      h.description?.toLowerCase().includes(term) ||
      h.chapter?.toLowerCase().includes(term) ||
      h.heading?.toLowerCase().includes(term) ||
      h.subHeading?.toLowerCase().includes(term)
    );
  }, [hsnCodes, searchTerm]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const paginated = filtered.slice(startIndex, startIndex + pageSize);

  const handleNew = () => navigate('/hsn/new');
  const handleEdit = (h: HsnCode) => navigate(`/hsn/${h.id}/edit`);
  const handleView = (h: HsnCode) => navigate(`/hsn/${h.id}/view`);
  const handleDelete = async (h: HsnCode) => {
    if (!h.id) return;
    if (window.confirm(`Delete HSN code ${h.hsnCode}?`)) {
      try {
        await hsnApiService.deleteHsnCode(String(h.id));
        setHsnCodes(prev => prev.filter(x => x.id !== h.id));
      } catch (e) {
        // toasted
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span>Loading HSN codes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary-200 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">HSN Master</CardTitle>
                <p className="text-gray-600 text-sm">Manage HSN codes and tax rates</p>
              </div>
            </div>
            <PermissionGate route={ROUTE} action="create">
              <Button onClick={handleNew} className="bg-primary-500 text-gray-700 hover:bg-gray-50 shadow-lg">
                <Plus className="h-4 w-4 mr-2" />
                New HSN Code
              </Button>
            </PermissionGate>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search HSN codes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">{filtered.length} of {hsnCodes.length} HSN codes</div>
          </div>

          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-primary-50">
                  <TableHead className="font-semibold text-primary-800">HSN Code</TableHead>
                  <TableHead className="font-semibold text-primary-800">Description</TableHead>
                  <TableHead className="font-semibold text-primary-800">Chapter</TableHead>
                  <TableHead className="font-semibold text-primary-800">Heading</TableHead>
                  <TableHead className="font-semibold text-primary-800">Sub Heading</TableHead>
                  <TableHead className="font-semibold text-primary-800">IGST Rate</TableHead>
                  <TableHead className="font-semibold text-primary-800">CGST Rate</TableHead>
                  <TableHead className="font-semibold text-primary-800">SGST Rate</TableHead>
                  <TableHead className="font-semibold text-primary-800">CESS Rate</TableHead>
                  <TableHead className="font-semibold text-primary-800">Status</TableHead>
                  <TableHead className="font-semibold text-primary-800 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                      No HSN codes found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((h) => (
                    <TableRow key={h.id} className="hover:bg-primary-50/50">
                      <TableCell className="font-medium">{h.hsnCode}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={h.description}>
                        {h.description || '-'}
                      </TableCell>
                      <TableCell>{h.chapter || '-'}</TableCell>
                      <TableCell>{h.heading || '-'}</TableCell>
                      <TableCell>{h.subHeading || '-'}</TableCell>
                      <TableCell>{h.currentIgstRate?.toFixed(2) || '0.00'}%</TableCell>
                      <TableCell>{h.currentCgstRate?.toFixed(2) || '0.00'}%</TableCell>
                      <TableCell>{h.currentSgstRate?.toFixed(2) || '0.00'}%</TableCell>
                      <TableCell>{h.currentCessRate?.toFixed(2) || '0.00'}%</TableCell>
                      <TableCell>
                        <Badge variant={h.isActive ? "default" : "secondary"} className={h.isActive ? "bg-green-100 text-green-700 border-green-200" : "bg-gray-100 text-gray-700 border-gray-200"}>
                          {h.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          <PermissionGate route={ROUTE} action="view">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(h)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ROUTE} action="edit">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(h)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ROUTE} action="delete">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(h)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

